import { Link } from "react-router-dom";
import React, { useState } from "react";
import {
  useStorageConfigs,
  useStorageTemplate,
  useCreateStorageConfig,
  useUpdateStorageConfigValue,
} from "../../../api/queries/adminQueries";
import { useBranchesByCompany } from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import { toast } from "react-toastify";
import "./DataSync.css";
import usePermissions from "../../../hooks/usePermissions";
import {
  NeedCompanyHint,
  useAllCompanies,
} from "../../../components/layout/WorkingCompany";

// Template fields with a friendlier label; OPTIONAL ones may stay empty.
const FIELD_LABELS = {
  assetsFolderId: "Assets folder ID",
  seminarsFolderId: "Seminars folder ID (attendance sheets)",
};
const OPTIONAL_FIELDS = new Set(["assetsFolderId", "seminarsFolderId"]);

const StorageConfig = () => {
  const localCompanyId = localStorage.getItem("companyId") || "";

  /* ===================== LOCAL STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState([]); // 400 messages (may be an array)

  const [formData, setFormData] = useState({
    id: "",
    provider: "",
    level: "company",
    branchId: "",
    isActive: false,
    configValue: {},
  });

  // Server data
  // GET /storage-configs (every company) is super admin only; HR reads their company's
  const { superAdmin } = usePermissions();
  const { data: configsData } = useStorageConfigs(
    superAdmin ? "all" : "company",
    { enabled: superAdmin || !!localCompanyId },
  );
  const allCompanies = useAllCompanies();
  const configs = Array.isArray(configsData)
    ? configsData
    : configsData?.data || [];
  const { data: branches = [] } = useBranchesByCompany(localCompanyId);
  // Field template for the selected provider (s3 / zoho); loads when a provider is picked.
  const { data: providerTemplate } = useStorageTemplate(formData.provider);
  const templates = providerTemplate
    ? { [formData.provider]: providerTemplate }
    : {};
  const createConfig = useCreateStorageConfig();
  const updateConfig = useUpdateStorageConfigValue();

  /* ===================== FILTER LOGIC ===================== */
  const filteredConfigs = configs.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      item.provider?.toLowerCase().includes(searchLower) ||
      item.branch?.branchName?.toLowerCase().includes(searchLower);
    const matchesLevel = levelFilter === "all" || item.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({
      id: "",
      provider: "",
      level: "company",
      branchId: "",
      isActive: false,
      configValue: {},
    });
    setFormErrors([]);
    setShowModal(true);
  };

  const handleOpenEdit = async (item) => {
    setIsEditMode(true);
    setFormData({
      id: item.id,
      provider: item.provider,
      level: item.level || "company",
      branchId: item.branch?.id || item.branchId || "",
      isActive: item.isActive,
      configValue: item.configValue || {},
    });
    setFormErrors([]);
    setShowModal(true);
  };

  const handleStatusToggle = async (item) => {
    try {
      await updateConfig.mutateAsync({ id: item.id, isActive: !item.isActive });
      toast.success("Status updated!");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Update failed"));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormErrors([]);
    try {
      if (isEditMode) {
        await updateConfig.mutateAsync({
          id: formData.id,
          configValue: formData.configValue,
          isActive: formData.isActive,
        });
      } else {
        // Only the fields the API accepts; branchId only for branch level
        await createConfig.mutateAsync({
          provider: formData.provider,
          level: formData.level,
          companyId: localCompanyId || undefined,
          ...(formData.level === "branch" && { branchId: formData.branchId }),
          configValue: formData.configValue,
          isActive: formData.isActive,
        });
      }
      setShowModal(false);
      toast.success("Saved successfully");
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (err?.response?.status === 400)
        setFormErrors(
          Array.isArray(msg) ? msg : [getApiErrorMessage(err, "Error saving")],
        );
      else toast.error(getApiErrorMessage(err, "Error saving"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <NeedCompanyHint action="add a storage provider for it (the list shows every company)" />
        {/* Header & Compact Filters Row */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Storage</h1>
            <p className="text-muted small mb-0">
              Manage global or branch-specific storage
            </p>
          </div>

          <div className="d-flex gap-2">
            <div className="search-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                className="form-control compact-filter search-input"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="form-select compact-filter w-160px"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="all">All Levels</option>
              <option value="company">Company</option>
              <option value="branch">Branch</option>
            </select>
            <button
              className="btn btn-dark compact-filter px-3"
              onClick={handleOpenAdd}
              disabled={allCompanies}
              title={
                allCompanies
                  ? "Pick a company at the top right first"
                  : undefined
              }
            >
              <i className="bi bi-plus-lg me-1"></i> Add Provider
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="storage-card shadow-sm p-0 overflow-hidden">
          <table className="table table-hover align-middle mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Storage Type</th>
                <th className="py-3">Apply To</th>
                <th className="py-3">Company</th>
                <th className="py-3">Date Added</th>
                <th className="py-3 text-center">Status</th>
                <th className="pe-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredConfigs.length > 0 ? (
                filteredConfigs.map((item) => (
                  <tr key={item.id}>
                    <td className="ps-4 fw-bold">
                      {item.provider?.toUpperCase()}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {item.level === "company" ? "Company" : "Branch"}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {item.level === "company"
                          ? item.company.companyName
                          : item.branch.branchName}
                      </span>
                    </td>
                    <td className="text-muted small">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "---"}
                    </td>
                    <td className="text-center">
                      <div className="form-check form-switch d-flex justify-content-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={item.isActive}
                          onChange={() => handleStatusToggle(item)}
                        />
                      </div>
                    </td>
                    <td className="pe-4 text-end">
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => handleOpenEdit(item)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal - Kept consistent with your logic */}
      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Add"} Storage</>}
          onClose={() => setShowModal(false)}
          size="md"
        >
          <form onSubmit={handleSave}>
            {formErrors.length > 0 && (
              <div className="alert alert-danger fs-080 py-2" role="alert">
                {formErrors.length === 1 ? (
                  formErrors[0]
                ) : (
                  <ul className="mb-0 ps-3">
                    {formErrors.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="mb-3">
              <label className="profile-label">STORAGE SERVICE</label>
              <select
                className="form-select mt-1"
                value={formData.provider}
                onChange={(e) => {
                  const p = e.target.value;
                  setFormData({ ...formData, provider: p }); // template loads via useStorageTemplate
                }}
                disabled={isEditMode}
                required
              >
                <option value="">-- Select --</option>
                <option value="s3">Amazon S3</option>
                <option value="zoho">Zoho WorkDrive</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="profile-label">APPLY TO</label>
              <select
                className="form-select mt-1"
                value={formData.level}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    level: e.target.value,
                    branchId: "",
                  })
                }
              >
                <option value="company">Entire Company</option>
                <option value="branch">Specific Branch</option>
              </select>
            </div>

            {formData.level === "branch" && (
              <div className="mb-3">
                <label className="profile-label">BRANCH NAME</label>
                <select
                  className="form-select mt-1"
                  value={formData.branchId}
                  onChange={(e) =>
                    setFormData({ ...formData, branchId: e.target.value })
                  }
                  required
                >
                  <option value="">-- Choose Branch --</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branchName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.provider && templates[formData.provider] && (
              <div className="row g-2">
                {Object.keys(templates[formData.provider]).map((key) => (
                  <div className="col-12" key={key}>
                    <label className="small text-muted text-capitalize">
                      {FIELD_LABELS[key] || key.replace(/([A-Z])/g, " $1")}
                      {OPTIONAL_FIELDS.has(key) && (
                        <span className="text-slate-400 text-lowercase">
                          {" "}
                          (optional)
                        </span>
                      )}
                    </label>
                    <input
                      type={
                        key.toLowerCase().includes("secret") ||
                        key.toLowerCase().includes("key")
                          ? "password"
                          : "text"
                      }
                      className="form-control mt-1"
                      value={formData.configValue[key] || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          configValue: {
                            ...prev.configValue,
                            [key]: e.target.value,
                          },
                        }))
                      }
                      required={!OPTIONAL_FIELDS.has(key)}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex gap-2 mt-4 pt-3 border-top">
              <button
                type="button"
                className="btn btn-light w-100"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-dark w-100"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </Layout>
  );
};

export default StorageConfig;
